import React from 'react';
import Layout from 'components/layout';
import HeadData from 'components/head-data';
import AgentPage from 'components/agent-search/agent-profile';
import {
  AGENTS_ROUTE,
} from 'components/dynamic-page/route-matchers';
import { trackEvent } from 'utils/google-tag-manager';
import {
  GTM_AGENT_JOIN_EXP_BUTTON_CLICK,
  GTM_AGENT_PROFILE_RETURN_TEXT_CLICK,
  GTM_AGENT_PROFILE_SERVICE_BUTTON_CLICK,
  GTM_AGENT_PROFILE_VIEW,
  GTM_CLICK_AGENT_PROFILE_COPY_EMAIL,
  GTM_CLICK_AGENT_PROFILE_COPY_PHONE,
} from 'constants/events';
import {
  PROFILE_LINKS_TO_GTM_EVENTS,
  type AgentSocialMediaLinks,
} from 'constants/agent-profile';
import {
  createGetServerSideProps,
  HandlerType,
} from 'utils/next/shared-getServerSideProps';
import { DynamicPageServerSideProps } from 'types';
import { isTenantExpCommercial } from '@tenants/utils/which-tenant';
import { NOT_FOUND } from 'constants/not-found';
import { getTenantById } from '@tenants/manager';
import { getTenantOrganizationConfig } from '@tenants/utils/configs';

import type { GetServerSidePropsContext } from 'next';

type DynamicAgentPageProps = DynamicPageServerSideProps<object, typeof AGENTS_ROUTE>;

const handler: HandlerType<DynamicAgentPageProps> = async (context: GetServerSidePropsContext, features, tenant) => {
  // If the tenant does not have the AgentSearch feature, return 404
  if (!tenant.features?.AgentSearch) {
    return NOT_FOUND;
  }

  const agentInfoFromQuery = context.query.dynamic && context.query.dynamic[0];
  const fullName = agentInfoFromQuery?.split('_')[0];
  const agentName = fullName?.replace('-', ' ');
  const headTags = {
    title: `${agentName} ${tenant.theme.title}`,
    metaDescription: tenant.theme.metaDescription,
  };

  return {
    props: {
      routeName: AGENTS_ROUTE,
      props: {
        headTags,
        features,
        tenantId: tenant.id,
      },
    },
  };
};

export const getServerSideProps = createGetServerSideProps(handler);

export const onSocialMediaLinkClick = (socialMediaType: AgentSocialMediaLinks) => {
  trackEvent(PROFILE_LINKS_TO_GTM_EVENTS[socialMediaType]);
};

const eventTrackers = {
  onSocialMediaLinkClick,
  onPageView: () => trackEvent(GTM_AGENT_PROFILE_VIEW),
  onGoBack: () => trackEvent(GTM_AGENT_PROFILE_RETURN_TEXT_CLICK),
  onCopyEmailClick: () => trackEvent(GTM_CLICK_AGENT_PROFILE_COPY_EMAIL),
  onCopyPhoneClick: () => trackEvent(GTM_CLICK_AGENT_PROFILE_COPY_PHONE),
  onExploreServicesClick: () => trackEvent(GTM_AGENT_PROFILE_SERVICE_BUTTON_CLICK),
  onJoinExpClick: () => trackEvent(GTM_AGENT_JOIN_EXP_BUTTON_CLICK),
};

export default function DynamicAgentPage({ props }: DynamicAgentPageProps) {
  const { headTags, tenantId } = props;
  const tenant = getTenantById(tenantId);
  const organizationConfig = getTenantOrganizationConfig(tenant);
  return (
    <Layout>
      {headTags && <HeadData data={{ title: headTags.title, metaDescription: headTags.metaDescription }} />}
      <AgentPage eventTrackers={eventTrackers}
        joinUsLink={organizationConfig?.joinSectionUrl}
        isCommercial={isTenantExpCommercial(tenant.id)} />
    </Layout>
  );
}
